import React, { useState } from "react";
import { imagesUrls } from "../../utils/constants";
import Footer from "../../components/Footer";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import DonateButton from "../../components/DonateButton";

type GameMode = "Solo" | "Multiplayer" | "Both";

type Game = {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  category: string;
  mode: GameMode;
  featured: boolean;
};

type Props = {};

export default function HomePage({}: Props) {
  // Sample games data
  const gamesData: Game[] = [
    {
      id: "cosmic-racer",
      title: "Cosmic Racer",
      description:
        "Race through space avoiding asteroids and collecting cosmic dust.",
      imageUrl:
        "https://mlpnk72yciwc.i.optimole.com/cqhiHLc.IIZS~2ef73/w:auto/h:auto/q:75/https://bleedingcool.com/wp-content/uploads/2024/12/Cosmic-Royale-Main-Art.jpg",
      category: "Action",
      mode: "Solo",
      featured: true,
    },
    {
      id: "wall-breaker",
      title: "Wall Breaker",
      description:
        "Break the wall by bouncing the ball against it. Collect power-ups and avoid obstacles.",
      imageUrl: imagesUrls["car"],
      category: "Strategy",
      mode: "Solo",
      featured: true,
    },
    {
      id: "tiktaktoe",
      title: "Tik Tak Toe",
      description:
        "Classic game of Tik Tak Toe, where you need to make a straight line to win.",
      imageUrl:
        "https://img.poki-cdn.com/cdn-cgi/image/quality=78,width=314,height=314,fit=cover,f=auto/591a911d9b73482c052270a8fe4cfc95.png",
      category: "Strategy",
      mode: "Solo",
      featured: true,
    },
    {
      id: "memory-match",
      title: "Memory Match",
      description: "Test your memory by matching pairs of cards.",
      imageUrl: imagesUrls["postCrafter2"],
      category: "Puzzle",
      mode: "Solo",
      featured: false,
    },
    {
      id: "game-of-life",
      title: "Game Of Life",
      description:
        "Experience the cellular automaton simulation where simple rules create complex, evolving patterns.",
      imageUrl:
        "https://www.metacritic.com/a/img/catalog/provider/6/3/6-1-676242-13.jpg",
      category: "Adventure",
      mode: "Solo",
      featured: true,
    },
    {
      id: "guess-number",
      title: "Guess the Number",
      description:
        "Try to guess the number selected by the game within a limited number of attempts.",
      imageUrl: imagesUrls["food"],
      category: "Action",
      mode: "Multiplayer",
      featured: true,
    },
  ];

  // State for search input
  const [searchQuery, setSearchQuery] = useState("");

  // State for category filter
  const [selectedCategory, setSelectedCategory] = useState("All");

  // Get unique categories
  const categories = [
    "All",
    ...Array.from(new Set(gamesData.map((game) => game.category))),
  ];

  // Filter games based on search query and selected category
  const filteredGames = gamesData.filter((game) => {
    const matchesSearch = game.title
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesCategory =
      selectedCategory === "All" || game.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Featured games
  const featuredGames = gamesData.filter((game) => game.featured);
  const navigate = useNavigate();

  // Play game function
  const handlePlayGame = (gameId: string) => {
    navigate("/game/" + gameId);
  };

  // See details function
  const handleSeeDetails = (gameId: string) => {
    console.log(`Viewing details for game with ID: ${gameId}`);
    // Implementation for showing game details
  };

  return (
    <div
      style={{
        fontFamily: "'Inter', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
        color: "#333",
        backgroundColor: "#f7f9fc",
        minHeight: "100vh",
        padding: "0",
        margin: "0",
        boxSizing: "border-box",
      }}
    >
      {/* Hero section */}
      <section
        style={{
          background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
          color: "white",
          padding: "2rem 1.5rem 4rem",
          textAlign: "center",
        }}
      >
        <div
          style={{
            maxWidth: "800px",
            margin: "0 auto",
          }}
        >
          <h2
            style={{
              fontSize: "2.5rem",
              fontWeight: "bold",
              marginBottom: "1rem",
            }}
          >
            Boost your Pi mining rate by playing games, anywhere, anytime
          </h2>
          <p
            style={{
              fontSize: "1.25rem",
              marginBottom: "2rem",
              opacity: "0.9",
            }}
          >
            No servers, no hassle. Just connect and play with friends using our
            peer-to-peer technology.
          </p>
          <DonateButton />

          {/* Search bar */}
          <div
            style={{
              display: "flex",
              maxWidth: "600px",
              margin: "0 auto",
              backgroundColor: "white",
              borderRadius: "9999px",
              padding: "0.5rem",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            }}
          >
            <input
              type="text"
              placeholder="Search for games..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                flex: "1",
                border: "none",
                padding: "0.75rem 1.5rem",
                fontSize: "1rem",
                borderRadius: "9999px",
                outline: "none",
              }}
            />
            <button
              style={{
                backgroundColor: "#6366F1",
                color: "white",
                border: "none",
                borderRadius: "9999px",
                padding: "0.75rem 1.5rem",
                fontWeight: "bold",
                cursor: "pointer",
                transition: "background-color 0.3s ease",
              }}
            >
              Search
            </button>
          </div>
        </div>
      </section>

      {/* Main content */}
      <main
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "2rem 1.5rem",
        }}
      >
        {/* Category filter */}
        <div
          style={{
            display: "flex",
            overflowX: "auto",
            gap: "0.75rem",
            padding: "0.5rem 0",
            marginBottom: "2rem",
            scrollbarWidth: "none", // Firefox
          }}
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              style={{
                backgroundColor:
                  selectedCategory === category ? "#6366F1" : "white",
                color: selectedCategory === category ? "white" : "#6366F1",
                border: "1px solid #6366F1",
                borderRadius: "9999px",
                padding: "0.5rem 1.25rem",
                fontSize: "0.875rem",
                fontWeight: "500",
                cursor: "pointer",
                whiteSpace: "nowrap",
                transition: "all 0.3s ease",
              }}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Featured games section */}
        {selectedCategory === "All" && searchQuery === "" && (
          <section style={{ marginBottom: "3rem" }}>
            <h3
              style={{
                fontSize: "1.5rem",
                fontWeight: "600",
                marginBottom: "1.5rem",
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              <span
                style={{
                  backgroundColor: "#6366F1",
                  width: "12px",
                  height: "24px",
                  borderRadius: "4px",
                  display: "inline-block",
                }}
              ></span>
              Featured Games
            </h3>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: "1.5rem",
              }}
            >
              {featuredGames.map((game) => (
                <GameCard
                  key={game.id}
                  game={game}
                  onPlay={handlePlayGame}
                  onSeeDetails={handleSeeDetails}
                  featured={true}
                />
              ))}
            </div>
          </section>
        )}

        {/* All games section */}
        <section>
          <h3
            style={{
              fontSize: "1.5rem",
              fontWeight: "600",
              marginBottom: "1.5rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
            }}
          >
            <span
              style={{
                backgroundColor: "#6366F1",
                width: "12px",
                height: "24px",
                borderRadius: "4px",
                display: "inline-block",
              }}
            ></span>
            {selectedCategory === "All"
              ? "All Games"
              : selectedCategory + " Games"}
          </h3>

          {filteredGames.length > 0 ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: "1.5rem",
              }}
            >
              {filteredGames.map((game) => (
                <GameCard
                  key={game.id}
                  game={game}
                  onPlay={handlePlayGame}
                  onSeeDetails={handleSeeDetails}
                  featured={false}
                />
              ))}
            </div>
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: "3rem 0",
                color: "#6B7280",
              }}
            >
              <p style={{ fontSize: "1.25rem", marginBottom: "1rem" }}>
                No games found matching your search.
              </p>
              <button
                onClick={() => {
                  setSearchQuery("");
                  setSelectedCategory("All");
                }}
                style={{
                  backgroundColor: "#6366F1",
                  color: "white",
                  border: "none",
                  borderRadius: "9999px",
                  padding: "0.75rem 1.5rem",
                  fontWeight: "500",
                  cursor: "pointer",
                }}
              >
                Reset Filters
              </button>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}

// Game Card Component
const GameCard = ({
  game,
  onPlay,
  onSeeDetails,
  featured,
}: {
  game: Game;
  onPlay: (id: string) => void;
  onSeeDetails: (id: string) => void;
  featured: boolean;
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        backgroundColor: "white",
        borderRadius: "16px",
        overflow: "hidden",
        boxShadow: isHovered
          ? "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          : "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        transform: isHovered ? "translateY(-8px)" : "translateY(0)",
        position: "relative",
      }}
    >
      {/* Game Image */}
      <div
        style={{
          position: "relative",
          height: "180px",
          overflow: "hidden",
        }}
      >
        <img
          src={game?.imageUrl}
          alt={game.title}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            transition: "transform 0.5s ease",
            transform: isHovered ? "scale(1.05)" : "scale(1)",
          }}
        />

        {/* Game mode badge */}
        <div
          style={{
            position: "absolute",
            top: "12px",
            left: "12px",
            backgroundColor:
              game.mode === "Solo"
                ? "#10B981"
                : game.mode === "Multiplayer"
                ? "#6366F1"
                : "#8B5CF6",
            color: "white",
            fontSize: "0.75rem",
            fontWeight: "500",
            padding: "0.25rem 0.75rem",
            borderRadius: "9999px",
          }}
        >
          {game.mode}
        </div>

        {/* Featured badge */}
        {game.featured && (
          <div
            style={{
              position: "absolute",
              top: "12px",
              right: "12px",
              backgroundColor: "#F59E0B",
              color: "white",
              fontSize: "0.75rem",
              fontWeight: "500",
              padding: "0.25rem 0.75rem",
              borderRadius: "9999px",
            }}
          >
            Featured
          </div>
        )}
      </div>

      {/* Game Info */}
      <div style={{ padding: "1.25rem" }}>
        <h4
          style={{
            fontSize: "1.25rem",
            fontWeight: "600",
            marginBottom: "0.5rem",
            color: "#111827",
          }}
        >
          {game.title}
        </h4>

        <p
          style={{
            fontSize: "0.875rem",
            color: "#6B7280",
            marginBottom: "1.25rem",
            lineHeight: "1.5",
            height: "40px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {game.description}
        </p>

        <div
          style={{
            display: "flex",
            gap: "0.75rem",
          }}
        >
          <button
            onClick={() => onPlay(game.id)}
            style={{
              flex: "1",
              backgroundColor: "#6366F1",
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "0.625rem",
              fontWeight: "500",
              cursor: "pointer",
              transition: "background-color 0.3s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 3L19 12L5 21V3Z"
                fill="white"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Play
          </button>

          <button
            onClick={() => onSeeDetails(game.id)}
            style={{
              backgroundColor: "white",
              color: "#6366F1",
              border: "1px solid #6366F1",
              borderRadius: "8px",
              padding: "0.625rem",
              fontWeight: "500",
              cursor: "pointer",
              transition: "background-color 0.3s ease, color 0.3s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Details
          </button>
        </div>
      </div>
    </div>
  );
};
